/**
 * 이상한 방 - 꾸미기 페이즈 (`place` · `unplace` · 하한 채움)
 *
 * 근거 문서: `이상한방-컨셉시트-v0.1.md` 3절(라운드 구조의 1페이즈 - 꾸미기 1분) · 5절(웃김 1·2 - 누가 놓은 변기)
 *            `이상한방-상태모델-v0.1.md` 2.1절(`placedBy`는 서버 전용) · 9.1절(하한 미달이면 프리셋 변주) ·
 *            9.2절(시스템 배치는 사람 배치와 같은 취급) · 11.3절(검증은 서버가 쥔다)
 *            로드맵 M10 "고정할 규칙" 넷
 *
 * ## 방을 사람이 만든다
 *
 * 다른 페이즈의 방은 생성기가 만든 것이고 라운드 내내 그대로다(9.5절: 방을 외우는 것이 스킬). 꾸미기만 다르다 -
 * **빈 셸에서 시작해 전원이 동시에 채우고, 그 결과가 이 매치의 `original`이 된다.** 그래서 이 파일이 만지는 것은
 * 초안이 아니라 `original` 자체다(바꾸기의 작업대와 정반대다 - 거기서는 남의 초안이 어떤 경로로도 가지 않는다).
 *
 * ## 이 파일에는 거부 사유의 비교식이 없다
 *
 * `matchChange.ts`와 같은 규약이다. 집행자는 `core/verbs.applyAdd` 하나이고, 인당 한도(`config.placementsPerPlayer`)도
 * 경계도 점유도 거기서 답이 나온다(`shouldEnforcePlacementLimit`를 **켜는** 유일한 자리가 여기다 - 체인저의 '추가'는
 * 꾸미기 배치가 아니라 그쪽은 끈다). 같은 셀에 둘이 놓으면 **선착만 서고** 늦은 쪽만 `cell-occupied`를 받는다 -
 * 호스트 큐가 도착 순으로 부르므로 "동시"가 없다(`net/matchHost.ts` 머리말).
 *
 * ## 걷는 것은 내 것만
 *
 * `unplace`는 그 물건의 `placedBy`가 나일 때만 선다. 남의 것을 걷을 수 있으면 꾸미기가 편집이 아니라 싸움이 되고,
 * 프리셋 채움(주인이 `system`)도 사람이 지울 수 없어야 한다 - 그건 하한 자체를 무너뜨린다.
 *
 * ## 하한은 프리셋 변주로 채운다 (9.1절)
 *
 * 셋이서 1분 동안 여섯 개를 놓고 끝낸 방은 방이 아니다. 그래서 마감 순간 **생성기가 만든 같은 시드의 방**에서
 * 물건을 순서대로 꺼내 하한까지 얹는다. 사람이 이미 차지한 칸의 것은 조용히 건너뛴다(`applyAdd`가 `cell-occupied`로
 * 답하고 그건 거부가 아니라 "그 자리는 이미 사람 것"이다). 새 생성 규칙을 쓰지 않는 이유는 결정론이다 - 같은 시드면
 * 같은 프리셋이고, 사람이 놓은 것만 판마다 다르다.
 *
 * **채운 것의 주인은 `system`이다**(9.2절). 인당 한도에 걸리지 않아야 하고, 리빌의 "누가 놓았나" 문장도 시스템 것에는
 * 붙지 않아야 한다.
 *
 * ## `placedBy`는 어떤 스냅샷 JSON에도 없다 (2.1절)
 *
 * 방이 와이어를 지날 때 `core/roomCodec.encodeRoom`이 그 필드를 떨어뜨린다. 그래서 놓인 물건을 실시간으로 알리는
 * 효과도 `WireObject`를 싣는다 - 누가 놓았는지는 **효과의 `by`**로 가고, 그것은 꾸미기 중에만 나가는 값이다
 * (수색 중에 방 데이터로 흘러가면 그 방 자체가 정답 지도가 된다).
 *
 * 이 파일은 `core`·`catalog`·`game` 리프만 본다(`boundary.test.ts`) - 서버가 그대로 import 한다.
 */

import { countByPlayer, createRoom, listObjects, removeObject } from '../../core/room'
import { generateBaseRoom, SYSTEM_PLACER } from '../../core/roomGen'
import { encodeObject } from '../../core/roomCodec'
import type { InstanceId, ObjectInstance, PlayerId, RoomState } from '../../core/types'
import { applyAdd, type VerbContext, type VerbRejection } from '../../core/verbs'
import { idle, matchPlan, roomOptionsFor } from './matchSelectors'
import type { MatchDeps, MatchState, MatchStep } from './matchTypes'

/* --- 하한 ------------------------------------------------------------------------ */

/**
 * 사람이 놓은 것이 이 비율에 못 미치면 프리셋으로 채운다. 1이면 "언제나 프리셋 방"이라 꾸미기가 장식이 되고,
 * 0이면 여섯 개짜리 방에서 수색이 돈다. 절반은 **사람이 놓은 것이 눈에 띄게 남는** 최소선이다 -
 * 값이 옳음의 문제가 아니라 실측의 문제라 튜닝으로 뺄 자리를 아래 함수 하나로 좁혀 둔다(7절 규약).
 */
const PRESET_FILL_RATIO = 0.5

/** 이 방이 가져야 할 최소 물건 수. 생성기의 목표 개수를 기준으로 잰다 - 평면도가 커지면 하한도 같이 커진다. */
export function decorateFloorCount(state: MatchState, deps: MatchDeps): number {
  return Math.round(presetRoom(state, deps).objects.size * PRESET_FILL_RATIO)
}

/* --- 방 ------------------------------------------------------------------------- */

/** 꾸미기가 시작되는 빈 방. 평면도(셸·벽·문)는 그대로이고 물건만 없다. */
export function emptyRoomFor(state: MatchState, deps: MatchDeps): RoomState {
  return createRoom(matchPlan(state, deps).id)
}

/**
 * 프리셋 - 이 매치의 시드로 생성기가 만든 방. 채움의 재료이고, 사람이 아무것도 안 놓으면 결과는 이것과 (거의) 같다.
 * 매번 다시 만드는 이유는 상태에 두면 스냅샷이 두 배가 되기 때문이다 - 마감에 한 번 부르는 함수다.
 */
function presetRoom(state: MatchState, deps: MatchDeps): RoomState {
  const plan = matchPlan(state, deps)
  return generateBaseRoom(plan, state.seed, deps.catalog, roomOptionsFor(plan, deps))
}

/** 채움·검증이 함께 쓰는 문맥. 인당 한도를 **켜는** 유일한 자리다(머리말). */
function decorateContext(state: MatchState, deps: MatchDeps): VerbContext {
  return {
    catalog: deps.catalog,
    config: state.config.room,
    bounds: matchPlan(state, deps).bounds,
    shouldEnforceOneChangePerObject: false,
    changedIds: new Set<InstanceId>(),
    // 꾸미기의 배치는 자유 배치다 - 머그를 책상 위에 올리는 것이 본업이라 높이를 잠그지 않는다.
    shouldKeepHeightOnMove: false,
    shouldEnforcePlacementLimit: true,
  }
}

/**
 * 하한까지 프리셋으로 채운 방(머리말 9.1절). 사람이 이미 하한을 넘겼으면 그대로다.
 *
 * 한도 검사는 끈다 - 채우는 주인이 `system` 하나라 켜 두면 `placementsPerPlayer`개에서 멈춘다.
 * 순서는 생성기가 낸 순서 그대로다(결정론). 막힌 것은 조용히 건너뛴다 - 사람이 그 자리를 이미 차지했다는 뜻이고,
 * 그건 거부가 아니라 꾸미기가 실제로 일어난 흔적이다.
 */
export function fillToFloor(room: RoomState, state: MatchState, deps: MatchDeps): RoomState {
  const floor = decorateFloorCount(state, deps)
  if (room.objects.size >= floor) return room

  const ctx: VerbContext = { ...decorateContext(state, deps), shouldEnforcePlacementLimit: false }
  let filled = room
  for (const obj of listObjects(presetRoom(state, deps))) {
    if (filled.objects.size >= floor) break
    // 사람이 같은 id를 만들 수는 없지만(도구가 다른 접두사를 쓴다) 와이어에서 온 값이라 방어한다 - `addObject`는 던진다.
    if (filled.objects.has(obj.id)) continue
    const result = applyAdd(filled, { ...obj, placedBy: SYSTEM_PLACER }, ctx)
    if (result.ok) filled = result.state
  }
  return filled
}

/* --- 이벤트 ---------------------------------------------------------------------- */

function rejectedPlace(state: MatchState, to: PlayerId, reason: VerbRejection): MatchStep {
  return { state, effects: [{ to, body: { type: 'placeRejected', reason } }] }
}

/** 지금 꾸밀 수 있는가. 페이즈 하나로 갈린다 - 좌석은 전원이 놓는다(M10: 동시 편집). */
export function canDecorate(state: MatchState, by: PlayerId): boolean {
  return state.phase === 'decorate' && state.players[by] !== undefined
}

/** 이 사람이 더 놓을 수 있는 개수. 세는 자리는 `countByPlayer` 하나이고 좌석에 사본을 두지 않는다. */
export function placementsLeftOf(state: MatchState, by: PlayerId): number {
  return Math.max(0, state.config.room.placementsPerPlayer - countByPlayer(state.original, by))
}

/**
 * 물건 하나를 놓는다. 게이트(참조 동일) → `applyAdd`(거부는 본인에게) → 방에 기록 + 전원에게 알림.
 *
 * `placedBy`는 **호스트가 찍는다**(2.1절) - 와이어의 값은 무시하고 보낸 사람의 신원으로 덮는다. 그래야
 * "남의 이름으로 놓기"가 프로토콜에 없다. 이미 있는 id는 참조 동일이다(`addObject`가 던지는 계약 위반이고,
 * 와이어에서 온 값으로 호스트가 던지면 안 된다 - `matchChange.onApplyChange`와 같은 판단).
 */
export function onPlace(
  state: MatchState,
  by: PlayerId,
  draft: Omit<ObjectInstance, 'placedBy'>,
  deps: MatchDeps,
): MatchStep {
  if (!canDecorate(state, by)) return idle(state)
  if (state.original.objects.has(draft.id)) return idle(state)

  const obj: ObjectInstance = { ...draft, placedBy: by }
  const result = applyAdd(state.original, obj, decorateContext(state, deps))
  if (!result.ok) return rejectedPlace(state, by, result.reason)

  // `mutated`도 함께 간다 - 꾸미기에는 변화가 없으므로 두 방은 같은 것이어야 한다(`startRound`가 같은 전제로 선다).
  const next: MatchState = { ...state, original: result.state, mutated: result.state }
  return {
    state: next,
    effects: [
      // 방 전체를 다시 보내지 않는다(`roomsChanged`는 페이즈 경계의 신호다) - 한 물건만 얹는다.
      // 실린 것은 `WireObject`라 `placedBy`가 없고, 누가 놓았는지는 효과의 `by`로만 간다(머리말 2.1절).
      { to: 'all', body: { type: 'placed', by, obj: encodeObject(obj) } },
    ],
  }
}

/**
 * 내가 놓은 것을 걷는다. 남의 것도 프리셋도 걷지 못한다(머리말) - 그 경우는 참조 동일이다.
 * 거부 효과를 내지 않는 이유는 화면이 애초에 남의 물건을 고를 수 없기 때문이다(지연이나 위장이다).
 */
export function onUnplace(state: MatchState, by: PlayerId, id: InstanceId, deps: MatchDeps): MatchStep {
  if (!canDecorate(state, by)) return idle(state)
  const target = state.original.objects.get(id)
  if (target === undefined || target.placedBy !== by) return idle(state)
  void deps

  const room = removeObject(state.original, id)
  const next: MatchState = { ...state, original: room, mutated: room }
  return { state: next, effects: [{ to: 'all', body: { type: 'unplaced', by, id } }] }
}
